import { Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useState, useCallback, lazy, Suspense } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "@/components/Navbar";
import HeroSection from "@/components/HeroSection";
import TrustBar from "@/components/TrustBar";
import ServicePillars from "@/components/ServicePillars";
import BuildProtect from "@/components/BuildProtect";
import FlynautEdge from "@/components/FlynautEdge";
import Industries from "@/components/Industries";
import SuccessStories from "@/components/SuccessStories";
import HowWeWork from "@/components/HowWeWork";
import InsightsPreview from "@/components/InsightsPreview";
import FinalConversion from "@/components/FinalConversion";
import AwardsPartners from "@/components/AwardsPartners";
import Footer from "@/components/Footer";
import NotificationBar from "@/components/NotificationBar";
import CookieConsent from "@/components/CookieConsent";
import NotFoundPage from "@/pages/NotFoundPage";
import ServiceSubPage from "@/pages/ServiceSubPage";
import { AdminAuthProvider } from "@/admin/AuthContext";
import { AdminThemeProvider } from "@/admin/ThemeContext";
import ProtectedRoute from "@/admin/components/ProtectedRoute";
import AdminLayout from "@/admin/components/AdminLayout";
import { cybersecuritySubPages } from "@/data/cybersecuritySubPages";
import { aiDataSubPages } from "@/data/aiDataSubPages";
import { cloudSubPages } from "@/data/cloudSubPages";
import { appDevSubPages } from "@/data/appDevSubPages";
import { modernizationSubPages } from "@/data/modernizationSubPages";
import { sustainabilitySubPages } from "@/data/sustainabilitySubPages";
import SEOHead from "@/components/SEOHead";
import Analytics from "@/components/Analytics";

// Lazy-loaded pages for faster initial load
const AssessmentPage = lazy(() => import("@/pages/AssessmentPage"));
const CybersecurityPage = lazy(() => import("@/pages/CybersecurityPage"));
const AppDevelopmentPage = lazy(() => import("@/pages/AppDevelopmentPage"));
const CloudPage = lazy(() => import("@/pages/CloudPage"));
const ModernizationPage = lazy(() => import("@/pages/ModernizationPage"));
const AIDataPage = lazy(() => import("@/pages/AIDataPage"));
const MVPPage = lazy(() => import("@/pages/MVPPage"));
const DataFoundationPage = lazy(() => import("@/pages/DataFoundationPage"));
const AgenticAIPage = lazy(() => import("@/pages/AgenticAIPage"));
const GenerativeAIPage = lazy(() => import("@/pages/GenerativeAIPage"));
const AnalyticsMLPage = lazy(() => import("@/pages/AnalyticsMLPage"));
const ResponsibleAIPage = lazy(() => import("@/pages/ResponsibleAIPage"));
const EmergingTechPage = lazy(() => import("@/pages/EmergingTechPage"));
const InsightsPage = lazy(() => import("@/pages/InsightsPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const CaseStudiesPage = lazy(() => import("@/pages/CaseStudiesPage"));
const IndustriesPage = lazy(() => import("@/pages/IndustriesPage"));
const ContactPage = lazy(() => import("@/pages/ContactPage"));
const CareersPage = lazy(() => import("@/pages/CareersPage"));
const PartnersPage = lazy(() => import("@/pages/PartnersPage"));
const EngagementModelsPage = lazy(() => import("@/pages/EngagementModelsPage"));
const ArticleDetailPage = lazy(() => import("@/pages/ArticleDetailPage"));
const CaseStudyDetailPage = lazy(() => import("@/pages/CaseStudyDetailPage"));
const IndustryDetailPage = lazy(() => import("@/pages/IndustryDetailPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const CookiesPage = lazy(() => import("@/pages/CookiesPage"));
const AccessibilityPage = lazy(() => import("@/pages/AccessibilityPage"));
const OurBrandsPage = lazy(() => import("@/pages/OurBrandsPage"));
const SustainabilityPage = lazy(() => import("@/pages/SustainabilityPage"));
const LeadershipPage = lazy(() => import("@/pages/LeadershipPage"));
const ValuesPage = lazy(() => import("@/pages/ValuesPage"));
const CareersJobsPage = lazy(() => import("@/pages/CareersJobsPage"));
const JobDetailPage = lazy(() => import("@/pages/JobDetailPage"));
const DiversityPage = lazy(() => import("@/pages/DiversityPage"));
const LifeAtFlynautPage = lazy(() => import("@/pages/LifeAtFlynautPage"));
const BenefitsPage = lazy(() => import("@/pages/BenefitsPage"));
const InternshipsPage = lazy(() => import("@/pages/InternshipsPage"));
const AdminLoginPage = lazy(() => import("@/admin/pages/LoginPage"));
const AdminAccessPending = lazy(() => import("@/admin/pages/AccessPendingPage"));
const AdminDashboard = lazy(() => import("@/admin/pages/DashboardPage"));
const AdminBlogList = lazy(() => import("@/admin/pages/BlogListPage"));
const AdminBlogEditor = lazy(() => import("@/admin/pages/BlogEditorPage"));
const AdminJobsList = lazy(() => import("@/admin/pages/JobsListPage"));
const AdminJobEditor = lazy(() => import("@/admin/pages/JobEditorPage"));
const AdminJobWizard = lazy(() => import("@/admin/pages/JobWizardPage"));
const AdminJobCRM = lazy(() => import("@/admin/pages/JobCRMPage"));
const AdminApplications = lazy(() => import("@/admin/pages/ApplicationsPage"));
const AdminAuditLog = lazy(() => import("@/admin/pages/AuditLogPage"));
const AdminSecurity = lazy(() => import("@/admin/pages/SecurityPage"));
const AdminCompliance = lazy(() => import("@/admin/pages/CompliancePage"));
const AdminAnalytics = lazy(() => import("@/admin/pages/AnalyticsPage"));
const AdminEmailSettings = lazy(() => import("@/admin/pages/EmailSettingsPage"));
const AdminHubSpot = lazy(() => import("@/admin/pages/HubSpotPage"));
const AdminCRMDashboard = lazy(() => import("@/admin/pages/CRMDashboardPage"));
const AdminLeadScore = lazy(() => import("@/admin/pages/LeadScorePage"));
const AdminSubmissions = lazy(() => import("@/admin/pages/SubmissionsPage"));
const AdminLeadDetail = lazy(() => import("@/admin/pages/LeadDetailPage"));
const AdminDigest = lazy(() => import("@/admin/pages/DigestPage"));
const AdminReportRecipients = lazy(() => import("@/admin/pages/ReportRecipientsPage"));
const AdminContentPages = lazy(() => import("@/admin/pages/ContentPagesPage"));
const AdminCaseStudiesAdmin = lazy(() => import("@/admin/pages/CaseStudiesAdminPage"));
const AdminContentCalendar = lazy(() => import("@/admin/pages/ContentCalendarPage"));
const AdminTelemetry = lazy(() => import("@/admin/pages/AdminTelemetryPage"));
const AdminAboutPages = lazy(() => import("@/admin/pages/AboutPagesPage"));
const AdminMediaLibrary = lazy(() => import("@/admin/pages/MediaLibraryPage"));
const AdminUsers = lazy(() => import("@/admin/pages/UsersPage"));
const AdminRoles = lazy(() => import("@/admin/pages/RolesPage"));
const AdminSEO = lazy(() => import("@/admin/pages/SEODashboardPage"));
const AdminSearchConsole = lazy(() => import("@/admin/pages/SearchConsolePage"));
const AdminMCP = lazy(() => import("@/admin/pages/MCPPage"));
const AdminLoginActivity = lazy(() => import("@/admin/pages/LoginActivityPage"));
const AdminContentAnalytics = lazy(() => import("@/admin/pages/ContentAnalyticsPage"));
const AdminCTAPerformance = lazy(() => import("@/admin/pages/CTAPerformancePage"));
const AdminDownloadHistory = lazy(() => import("@/admin/pages/DownloadHistoryPage"));
const AdminCardAuthorizations = lazy(() => import("@/admin/pages/CardAuthorizationsPage"));
const AdminLeadJourney = lazy(() => import("@/admin/pages/LeadJourneyPage"));
const AdminHRMSEmployees = lazy(() => import("@/admin/pages/HRMSEmployeesPage"));
const AdminHRMSEmployeeProfile = lazy(() => import("@/admin/pages/HRMSEmployeeProfilePage"));
const AdminHRMSAttendance = lazy(() => import("@/admin/pages/HRMSAttendancePage"));
const AdminHRMSLeave = lazy(() => import("@/admin/pages/HRMSLeavePage"));
const AdminHRMSPayroll = lazy(() => import("@/admin/pages/HRMSPayrollPage"));
const AdminHRMSReviews = lazy(() => import("@/admin/pages/HRMSReviewsPage"));
const AdminHRMSOnboarding = lazy(() => import("@/admin/pages/HRMSOnboardingPage"));
const AdminHRMSOfferLetters = lazy(() => import("@/admin/pages/HRMSOfferLettersPage"));
const AdminHRMSOffboarding = lazy(() => import("@/admin/pages/HRMSOffboardingPage"));
const AdminHRMSHolidays = lazy(() => import("@/admin/pages/HRMSHolidaysPage"));
const AdminAnnouncements = lazy(() => import("@/admin/pages/AnnouncementsPage"));
const AdminHRContacts = lazy(() => import("@/admin/pages/HRContactsPage"));
const AdminFlynautDrive = lazy(() => import("@/admin/pages/FlynautDrivePage"));
const BookPage = lazy(() => import("@/pages/BookPage"));

function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-white">
      <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
    </div>
  );
}

const homeSchema = [
  {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Flynaut",
    url: "https://flynaut.com",
    logo: "https://cdn.flynaut.com/images/Logo.png",
    description: "Flynaut builds, protects, and modernizes your technology. AI, apps, cloud, cybersecurity, automation. One partner. 20+ years.",
    contactPoint: { "@type": "ContactPoint", contactType: "sales", email: "info@flynaut.com" },
    sameAs: [],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Flynaut",
    url: "https://flynaut.com",
  },
];

function HomePage() {
  return (
    <div className="bg-white min-h-screen">
      <SEOHead
        title="Flynaut"
        description="Flynaut builds, protects, and modernizes your technology. AI, apps, cloud, cybersecurity, automation. One partner. 20+ years. Free assessment."
        canonical="/"
        schema={homeSchema}
      />
      <a href="#hero" className="skip-nav">Skip to main content</a>
      <Navbar />
      <main id="main-content">
        <HeroSection />
        <TrustBar />
        <ServicePillars />
        <BuildProtect />
        <FlynautEdge />
        <Industries />
        <SuccessStories />
        <HowWeWork />
        <InsightsPreview />
        <FinalConversion />
        <AwardsPartners />
      </main>
      <Footer />
    </div>
  );
}

function AdminPage({ children }) {
  return (
    <AdminThemeProvider>
      <AdminAuthProvider>
        <ProtectedRoute>
          <AdminLayout>{children}</AdminLayout>
        </ProtectedRoute>
      </AdminAuthProvider>
    </AdminThemeProvider>
  );
}

// Detect if running on admin-only domain (Railway deployment)
const isAdminDomain = typeof window !== 'undefined' &&
  (window.location.hostname.includes('admingate') || window.location.hostname.includes('admin.'));

function App() {
  const [notifHeight, setNotifHeight] = useState(0);
  const handleNotifHeight = useCallback((h) => setNotifHeight(h), []);
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  return (
    <>
      {/* Global Organization Schema */}
      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          name: "Flynaut",
          url: "https://flynaut.com",
          logo: "https://cdn.flynaut.com/images/Logo.png",
          sameAs: ["https://www.linkedin.com/company/flynaut/", "https://twitter.com/flynaut"],
          contactPoint: { "@type": "ContactPoint", telephone: "+1-312-586-1308", contactType: "sales", availableLanguage: "English" },
          address: { "@type": "PostalAddress", addressLocality: "Chicago", addressRegion: "IL", addressCountry: "US" }
        })}</script>
      </Helmet>
      {!isAdmin && <NotificationBar onHeightChange={handleNotifHeight} />}
      <Analytics />
      <div style={{ paddingTop: !isAdmin && notifHeight > 0 ? `${notifHeight}px` : undefined }}>
        <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={isAdminDomain ? <Navigate to="/admin/login" replace /> : <HomePage />} />
          <Route path="/assessment" element={<AssessmentPage />} />
          <Route path="/cybersecurity" element={<CybersecurityPage />} />
          <Route path="/services/application-development" element={<AppDevelopmentPage />} />
          <Route path="/services/cloud" element={<CloudPage />} />
          <Route path="/services/modernization-automation" element={<ModernizationPage />} />
          <Route path="/services/ai-data" element={<AIDataPage />} />
          <Route path="/services/ai-data/data-foundation" element={<DataFoundationPage />} />
          <Route path="/services/ai-data/agentic-ai" element={<AgenticAIPage />} />
          <Route path="/services/ai-data/generative-ai" element={<GenerativeAIPage />} />
          <Route path="/services/ai-data/analytics-ml" element={<AnalyticsMLPage />} />
          <Route path="/services/ai-data/responsible-ai" element={<ResponsibleAIPage />} />
          <Route path="/services/application-development/mvp-development" element={<MVPPage />} />
          <Route path="/insights" element={<InsightsPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/case-studies" element={<CaseStudiesPage />} />
          <Route path="/industries" element={<IndustriesPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/book" element={<BookPage />} />
          <Route path="/careers" element={<CareersPage />} />
          <Route path="/partners" element={<PartnersPage />} />
          <Route path="/engagement-models" element={<EngagementModelsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/cookies" element={<CookiesPage />} />
          <Route path="/accessibility" element={<AccessibilityPage />} />
          <Route path="/about/brands" element={<OurBrandsPage />} />
          <Route path="/about/leadership" element={<LeadershipPage />} />
          <Route path="/about/values" element={<ValuesPage />} />
          <Route path="/careers/jobs" element={<CareersJobsPage />} />
          <Route path="/careers/jobs/:slug" element={<JobDetailPage />} />
          <Route path="/careers/life-at-flynaut" element={<LifeAtFlynautPage />} />
          <Route path="/careers/benefits" element={<BenefitsPage />} />
          <Route path="/careers/internships" element={<InternshipsPage />} />
          <Route path="/about/diversity" element={<DiversityPage />} />
          <Route path="/services/sustainability" element={<SustainabilityPage />} />
          <Route path="/insights/:slug" element={<ArticleDetailPage />} />
          <Route path="/case-studies/:slug" element={<CaseStudyDetailPage />} />
          <Route path="/industries/:slug" element={<IndustryDetailPage />} />
          {cybersecuritySubPages.map((p) => (
            <Route key={p.slug} path={p.url} element={<ServiceSubPage data={p} />} />
          ))}
          {aiDataSubPages.map((p) => (
            <Route key={p.slug} path={p.url} element={<ServiceSubPage data={p} />} />
          ))}
          {cloudSubPages.map((p) => (
            <Route key={p.slug} path={p.url} element={<ServiceSubPage data={p} />} />
          ))}
          {appDevSubPages.map((p) => (
            <Route key={p.slug} path={p.url} element={<ServiceSubPage data={p} />} />
          ))}
          <Route path="/services/modernization-automation/emerging-tech" element={<EmergingTechPage />} />
          {modernizationSubPages.filter(p => p.slug !== 'emerging-tech').map((p) => (
            <Route key={p.slug} path={p.url} element={<ServiceSubPage data={p} />} />
          ))}
          {sustainabilitySubPages.map((p) => (
            <Route key={p.slug} path={p.url} element={<ServiceSubPage data={p} />} />
          ))}
          {/* Admin Panel */}
          <Route path="/admin/login" element={<AdminAuthProvider><AdminLoginPage /></AdminAuthProvider>} />
          <Route path="/admin/access-pending" element={<AdminAuthProvider><AdminAccessPending /></AdminAuthProvider>} />
          <Route path="/admin" element={<AdminPage><AdminDashboard /></AdminPage>} />
          <Route path="/admin/analytics" element={<AdminPage><AdminAnalytics /></AdminPage>} />
          <Route path="/admin/blog" element={<AdminPage><AdminBlogList /></AdminPage>} />
          <Route path="/admin/blog/new" element={<AdminPage><AdminBlogEditor /></AdminPage>} />
          <Route path="/admin/blog/:id" element={<AdminPage><AdminBlogEditor /></AdminPage>} />
          <Route path="/admin/jobs" element={<AdminPage><AdminJobsList /></AdminPage>} />
          <Route path="/admin/jobs/new" element={<AdminPage><AdminJobWizard /></AdminPage>} />
          <Route path="/admin/jobs/new-legacy" element={<AdminPage><AdminJobEditor /></AdminPage>} />
          <Route path="/admin/jobs/:id" element={<AdminPage><AdminJobEditor /></AdminPage>} />
          <Route path="/admin/jobs/:id/crm" element={<AdminPage><AdminJobCRM /></AdminPage>} />
          <Route path="/admin/applications" element={<AdminPage><AdminApplications /></AdminPage>} />
          <Route path="/admin/email-settings" element={<AdminPage><AdminEmailSettings /></AdminPage>} />
          <Route path="/admin/audit" element={<AdminPage><AdminAuditLog /></AdminPage>} />
          <Route path="/admin/security" element={<AdminPage><AdminSecurity /></AdminPage>} />
          <Route path="/admin/compliance" element={<AdminPage><AdminCompliance /></AdminPage>} />
          <Route path="/admin/hubspot" element={<AdminPage><AdminHubSpot /></AdminPage>} />
          <Route path="/admin/crm-dashboard" element={<AdminPage><AdminCRMDashboard /></AdminPage>} />
          <Route path="/admin/leads" element={<AdminPage><AdminLeadScore /></AdminPage>} />
          <Route path="/admin/submissions" element={<AdminPage><AdminSubmissions /></AdminPage>} />
          <Route path="/admin/leads/:source/:id" element={<AdminPage><AdminLeadDetail /></AdminPage>} />
          <Route path="/admin/digest" element={<AdminPage><AdminDigest /></AdminPage>} />
          <Route path="/admin/report-recipients" element={<AdminPage><AdminReportRecipients /></AdminPage>} />
          <Route path="/admin/content-pages" element={<AdminPage><AdminContentPages /></AdminPage>} />
          <Route path="/admin/case-studies" element={<AdminPage><AdminCaseStudiesAdmin /></AdminPage>} />
          <Route path="/admin/content-calendar" element={<AdminPage><AdminContentCalendar /></AdminPage>} />
          <Route path="/admin/telemetry" element={<AdminPage><AdminTelemetry /></AdminPage>} />
          <Route path="/admin/about-pages" element={<AdminPage><AdminAboutPages /></AdminPage>} />
          <Route path="/admin/media" element={<AdminPage><AdminMediaLibrary /></AdminPage>} />
          <Route path="/admin/users" element={<AdminPage><AdminUsers /></AdminPage>} />
          <Route path="/admin/roles" element={<AdminPage><AdminRoles /></AdminPage>} />
          <Route path="/admin/seo" element={<AdminPage><AdminSEO /></AdminPage>} />
          <Route path="/admin/search-console" element={<AdminPage><AdminSearchConsole /></AdminPage>} />
          <Route path="/admin/mcp" element={<AdminPage><AdminMCP /></AdminPage>} />
          <Route path="/admin/login-activity" element={<AdminPage><AdminLoginActivity /></AdminPage>} />
          <Route path="/admin/content-analytics" element={<AdminPage><AdminContentAnalytics /></AdminPage>} />
          <Route path="/admin/cta-performance" element={<AdminPage><AdminCTAPerformance /></AdminPage>} />
          <Route path="/admin/download-history" element={<AdminPage><AdminDownloadHistory /></AdminPage>} />
          <Route path="/admin/card-authorizations" element={<AdminPage><AdminCardAuthorizations /></AdminPage>} />
          <Route path="/admin/hrms/employees" element={<AdminPage><AdminHRMSEmployees /></AdminPage>} />
          <Route path="/admin/hrms/employees/:id/profile" element={<AdminPage><AdminHRMSEmployeeProfile /></AdminPage>} />
          <Route path="/admin/hrms/attendance" element={<AdminPage><AdminHRMSAttendance /></AdminPage>} />
          <Route path="/admin/hrms/leave" element={<AdminPage><AdminHRMSLeave /></AdminPage>} />
          <Route path="/admin/hrms/payroll" element={<AdminPage><AdminHRMSPayroll /></AdminPage>} />
          <Route path="/admin/hrms/reviews" element={<AdminPage><AdminHRMSReviews /></AdminPage>} />
          <Route path="/admin/hrms/onboarding" element={<AdminPage><AdminHRMSOnboarding /></AdminPage>} />
          <Route path="/admin/hrms/offers" element={<AdminPage><AdminHRMSOfferLetters /></AdminPage>} />
          <Route path="/admin/hrms/offboarding" element={<AdminPage><AdminHRMSOffboarding /></AdminPage>} />
          <Route path="/admin/hrms/holidays" element={<AdminPage><AdminHRMSHolidays /></AdminPage>} />
          <Route path="/admin/announcements" element={<AdminPage><AdminAnnouncements /></AdminPage>} />
          <Route path="/admin/hr-contacts" element={<AdminPage><AdminHRContacts /></AdminPage>} />
          <Route path="/admin/drive" element={<AdminPage><AdminFlynautDrive /></AdminPage>} />
          <Route path="/admin/lead-journey" element={<AdminPage><AdminLeadJourney /></AdminPage>} />
          <Route path="*" element={isAdminDomain ? <Navigate to="/admin/login" replace /> : <NotFoundPage />} />
        </Routes>
        </Suspense>
      </div>
      {!isAdmin && <CookieConsent />}
    </>
  );
}

export default App;
